/* eslint-disable max-lines */
import {
  Checkbox,
  CheckboxGroup,
  Chip,
  Input,
  Select,
  SelectItem,
} from '@heroui/react';
import React, { FC, useEffect } from 'react';
import { Controller, useForm } from 'react-hook-form';

import {
  ActionBlock,
  ContentLoadError,
  FormErrorGroup,
  Grid,
  inputProps,
  PageWrapper,
  Spinner,
} from '../../../../components';
import {
  useGetOrganizationsFromFirestore,
  useQueryHelper,
} from '../../../../queries';
import {
  AdminPermission,
  adminPermissionLabels,
  adminPermissionsArray,
  Language,
  languageLabels,
  languagesArray,
  staffServicePermissionLabels,
  staffServicePermissionsArray,
  UserDataFirebaseRequest,
  UserDataFirebaseResponse,
} from '../../../../types';
import { normalizePhoneE164 } from '../../../../utils/normalizePhone';
import { UserEntity, userFormOptions } from './CreateUserForm.schema';

interface CreateUserFormProps {
    isEdit?: boolean;
    onSubmit: (formState: UserDataFirebaseRequest) => void;
    initialValue?: UserDataFirebaseResponse | null | undefined;
}

const MAX_PERMISSIONS = 5;

/** Pozycja w selekcie oznaczająca brak zawężenia — Select nie ma pustego wyboru. */
const NO_ADMIN_SCOPE = 'full';

type OrganizationOption = { id: string; name?: string; companyName?: string; hotel?: string };

const organizationLabel = (org: OrganizationOption): string => (
  org.name || org.companyName || org.hotel || org.id
);

export const CreateUserForm: FC<CreateUserFormProps> = ({
  isEdit,
  onSubmit,
  initialValue,
}) => {
  const {
    formState,
    handleSubmit,
    register,
    control,
    watch,
    setValue,
  } = useForm<UserEntity>({
    ...userFormOptions,
    defaultValues: {
      name: initialValue?.name || '',
      surname: initialValue?.surname || '',
      email: initialValue?.email || '',
      role: 'user', // Always set to 'user'
      associated_organization: initialValue?.associated_organization || '',
      organizations: Array.isArray(initialValue?.organizations) ? initialValue?.organizations : [],
      phoneNumber: initialValue?.phoneNumber || '',
      permissions: ['all'], // Always set to ['all']
      // eslint-disable-next-line max-len
      staffServicePermissions: Array.isArray(initialValue?.staffServicePermissions) ? initialValue?.staffServicePermissions : [],
      owner: Array.isArray(initialValue?.owner) ? initialValue?.owner : [],
      isHotelspotAdmin: initialValue?.isHotelspotAdmin || false,
      adminPermission: initialValue?.adminPermission || undefined,
      language: initialValue?.language || 'pl',
    },
  });
  const organizations = watch('organizations') || [];
  const owner = watch('owner') || [];
  const isHotelspotAdmin = watch('isHotelspotAdmin') || false;

  const organizationQuery = useGetOrganizationsFromFirestore();
  const { data: organization, refetch, isLoading } = organizationQuery;
  const { isError, errorMessage } = useQueryHelper({
    queries: [organizationQuery],
  });

  // Permissions are always ['all'] now, no need to expand or modify

  // Automatically set associated_organization to the first selected organization
  useEffect(() => {
    if (organizations && organizations.length > 0) {
      setValue('associated_organization', organizations[0]);
    } else {
      setValue('associated_organization', '');
    }
  }, [organizations, setValue]);

  // Odpięcie organizacji zdejmuje też własność — backend odrzuca ownera spoza listy.
  useEffect(() => {
    const stillAssigned = owner.filter((id) => organizations.includes(id));
    if (stillAssigned.length !== owner.length) {
      setValue('owner', stillAssigned);
    }
  }, [organizations, owner, setValue]);

  if (isLoading) {
    return <Spinner global />;
  }

  if (isError) {
    return (
      <PageWrapper>
        <ContentLoadError
          onRefreshClicked={() => {
            refetch();
          }}
          message={errorMessage}
        />
      </PageWrapper>
    );
  }

  const handleFormSubmit = (formData: UserEntity): void => {
    // Każde konto zakładane/edytowane w panelu to personel — stąd isStaff i
    // staffService na sztywno; backend dokłada do tego claim `isStaff`.
    const dataToSend: UserDataFirebaseRequest = {
      ...formData,
      role: ['user'], // Always set to 'user' role array for backend
      permissions: ['all'], // Always set to ['all'] permissions
      isStaff: true,
      staffService: true,
      staffServicePermissions: formData.staffServicePermissions || [],
      owner: formData.owner || [],
      isHotelspotAdmin: formData.isHotelspotAdmin || false,
      // Zawężenie ma sens tylko przy koncie admina — inaczej czyścimy.
      adminPermission: formData.isHotelspotAdmin ? (formData.adminPermission ?? null) : null,
    };

    onSubmit(dataToSend);
  };

  return (
    <>
      <FormErrorGroup errors={formState.errors} />
      <form
        data-testid="form-create-user"
        noValidate
        id="form-create-user"
        onSubmit={handleSubmit(handleFormSubmit)}
      >
        {/* Basic Information */}
        <ActionBlock>
          <h3 className="text-lg font-semibold mb-4">Basic Information</h3>
          <Grid
            gridTemplateColumns="3fr 5fr"
            justifyContent="start"
            itemAlign="start"
            gap="25px"
          >
            <Input
              {...inputProps}
              {...register('name')}
              placeholder="Enter name"
              type="text"
              label="Name"
              isInvalid={!!formState.errors.name}
              errorMessage={formState.errors.name?.message}
            />
            <Input
              {...inputProps}
              {...register('surname')}
              placeholder="Enter surname"
              type="text"
              label="Surname"
              isInvalid={!!formState.errors.surname}
              errorMessage={formState.errors.surname?.message}
            />
            <Input
              {...inputProps}
              {...register('email')}
              placeholder="Enter email"
              type="text"
              label="Email"
              isInvalid={!!formState.errors.email}
              errorMessage={formState.errors.email?.message}
            />
            {/* Telefon = login do aplikacji Staff (SMS OTP). Bez sztywnej maski —
                ta psuła kierunkowe inne niż 2-cyfrowe; numer sprowadzamy do E.164
                przy wyjściu z pola i jeszcze raz w schemacie przed wysyłką. */}
            <Controller
              control={control}
              name="phoneNumber"
              render={({ field }) => (
                <Input
                  {...inputProps}
                  placeholder="+48 512 345 678"
                  type="tel"
                  label="Phone (opcjonalnie)"
                  value={field.value ?? ''}
                  onValueChange={field.onChange}
                  onBlur={() => field.onChange(normalizePhoneE164(field.value ?? ''))}
                  description="Opcjonalny. Podany numer zapisujemy w formacie E.164 (np. +48512345678) — to login do aplikacji Staff."
                  isInvalid={!!formState.errors.phoneNumber}
                  errorMessage={formState.errors.phoneNumber?.message}
                />
              )}
            />

            {organization?.data && organization.data.length > 0 && (
            <Controller
              control={control}
              name="organizations"
              rules={{
                required: 'Pole organizacje jest wymagane',
                validate: (value) => (Array.isArray(value) && value.length > 0
                  ? true
                  : 'Musisz wybrać co najmniej jedną organizację'),
              }}
              render={({ field }) => (
                <Select
                  size="lg"
                  label="Organizations"
                  variant="bordered"
                  radius="sm"
                  labelPlacement="outside"
                  selectionMode="multiple"
                  placeholder="Select organizations"
                  isInvalid={!!formState.errors.organizations}
                  errorMessage={formState.errors.organizations?.message}
                  selectedKeys={new Set(field.value || [])}
                  onSelectionChange={(keys) => field.onChange(Array.from(keys))}
                  isMultiline
                  renderValue={(items) => (
                    <div className="flex flex-wrap gap-2 m-2 text-small">
                      {items && items?.map((item) => (
                        <Chip key={item.key} className="whitespace-nowrap overflow-hidden text-ellipsis">{item.textValue}</Chip>
                      ))}
                    </div>
                  )}
                >
                  {organization.data.map((org) => (
                    <SelectItem key={org.id}>
                      {org.name || org.companyName || org.hotel || org.id}
                    </SelectItem>
                  ))}
                </Select>
              )}
            />
            )}

            <Controller
              control={control}
              name="language"
              render={({ field }) => (
                <Select
                  size="lg"
                  label="Language"
                  variant="bordered"
                  radius="sm"
                  labelPlacement="outside"
                  selectionMode="single"
                  placeholder="Select language"
                  selectedKeys={field.value ? new Set([field.value]) : new Set()}
                  onSelectionChange={(keys) => {
                    const selected = Array.from(keys)[0] as Language;
                    field.onChange(selected);
                  }}
                >
                  {languagesArray.map((lang) => (
                    <SelectItem key={lang}>
                      {languageLabels[lang]}
                    </SelectItem>
                  ))}
                </Select>
              )}
            />

          </Grid>
        </ActionBlock>

        {/* Role and Permissions are always set to 'user' and ['all'] respectively - no UI needed */}

        {/* Właściciel: checkbox per zaznaczona organizacja — zaznaczenie znaczy,
            że TEN user jest właścicielem tej organizacji. Zapisuje
            `organizations/{id}.owner = uid` i claim `owner`. */}
        {organizations.length > 0 && (
          <ActionBlock>
            <h3 className="text-lg font-semibold mb-4">Właściciel</h3>
            <Controller
              control={control}
              name="owner"
              render={({ field }) => (
                <CheckboxGroup
                  label="Ten użytkownik jest właścicielem organizacji:"
                  value={field.value || []}
                  onValueChange={(value) => field.onChange(value as string[])}
                  className="flex flex-col gap-3"
                >
                  {(organization?.data ?? [])
                    .filter((org) => organizations.includes(org.id))
                    .map((org) => (
                      <Checkbox key={org.id} value={org.id}>
                        {organizationLabel(org)}
                      </Checkbox>
                    ))}
                </CheckboxGroup>
              )}
            />
          </ActionBlock>
        )}

        {/* Uprawnienia — każde konto z panelu to personel, więc nie ma już
            przełącznika „czy staff", zostaje sam wybór modułów. */}
        <ActionBlock>
          <h3 className="text-lg font-semibold mb-4">Uprawnienia</h3>
          <div className="flex flex-col gap-6">
            <Controller
              control={control}
              name="staffServicePermissions"
              render={({ field }) => {
                const selectedPermissions = field.value || [];
                return (
                  <CheckboxGroup
                    label={`Moduły aplikacji Staff (max ${MAX_PERMISSIONS})`}
                    onValueChange={(value) => {
                      const next = value as string[];
                      if (next.length <= MAX_PERMISSIONS) {
                        field.onChange(next);
                      }
                    }}
                    value={selectedPermissions}
                    className="flex flex-col gap-3"
                  >
                    {staffServicePermissionsArray && staffServicePermissionsArray.map((permission) => (
                      <Checkbox
                        key={permission}
                        value={permission}
                        isDisabled={
                          selectedPermissions.length >= MAX_PERMISSIONS
                          && !selectedPermissions.includes(permission)
                        }
                      >
                        {staffServicePermissionLabels[permission]}
                      </Checkbox>
                    ))}
                  </CheckboxGroup>
                );
              }}
            />
          </div>
        </ActionBlock>

        {/* Administrator HotelSpot — claim `isHotelspotAdmin` + pole w `users/{uid}`. */}
        <ActionBlock>
          <div className="flex flex-col gap-4">
            <Controller
              control={control}
              name="isHotelspotAdmin"
              render={({ field }) => (
                <Checkbox
                  isSelected={field.value || false}
                  onValueChange={field.onChange}
                >
                  hotelspotAdmin
                </Checkbox>
              )}
            />

            {/* Zawężenie widoku panelu dla admina — puste = pełny dostęp. */}
            {isHotelspotAdmin && (
              <Controller
                control={control}
                name="adminPermission"
                render={({ field }) => (
                  <Select
                    size="lg"
                    label="adminPermission"
                    variant="bordered"
                    radius="sm"
                    labelPlacement="outside"
                    selectionMode="single"
                    placeholder="Pełny dostęp (bez zawężenia)"
                    description="onlyAttraction — tylko organizacje i atrakcje (bez akcji). onlySupport — tylko zgłoszenia."
                    className="max-w-md"
                    disallowEmptySelection
                    selectedKeys={new Set([field.value ?? NO_ADMIN_SCOPE])}
                    onSelectionChange={(keys) => {
                      const selected = Array.from(keys)[0] as string | undefined;
                      // Pozycja „pełny dostęp" zdejmuje zawężenie — backend dostaje
                      // wtedy `null` i kasuje claim `adminPermission`.
                      field.onChange(
                        selected && selected !== NO_ADMIN_SCOPE
                          ? (selected as AdminPermission)
                          : undefined,
                      );
                    }}
                  >
                    <SelectItem key={NO_ADMIN_SCOPE}>
                      Pełny dostęp (bez zawężenia)
                    </SelectItem>
                    <>
                      {adminPermissionsArray.map((permission) => (
                        <SelectItem key={permission}>
                          {adminPermissionLabels[permission]}
                        </SelectItem>
                      ))}
                    </>
                  </Select>
                )}
              />
            )}
          </div>
        </ActionBlock>
      </form>
    </>
  );
};
